import React, { FunctionComponent, useMemo } from 'react';
import classNames from 'classnames';
import checkGreenCircleImage from '../../../assets/check-green-circle.png';
import { Button } from '../../Input/Button/Button';
import { Checkbox } from '../../Input/Checkbox/Checkbox';
import { TextField } from '../../Input/TextField/TextField';
import { DealerLaunchStepProps } from '../types';
// eslint-disable-next-line import/no-cycle
import {
  getActiveDealerLaunchSections,
  getStepCompletePercent,
  isProductSelectComplete,
  isReadyToLaunch,
} from '../steps';
import { LinkButton } from '../../Input/Button/LinkButton';
import { ROUTES } from '../../../utils/routes';
import { QuickGuide } from '../../QuickGuide/QuickGuide';
import styles from '../styles.module.scss';

export const DealerLaunchHome: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  nextStep,
}) => {
  const initialWelcome = !isProductSelectComplete(dealer);

  // calculate the progress for all steps completed
  const stepCompletePercent = useMemo(() => getStepCompletePercent(dealer), [
    dealer,
  ]);

  // get the progress status for all sections
  const sectionProgress = useMemo(() => {
    const result = getActiveDealerLaunchSections(dealer).map((section) => ({
      name: section.name,
      complete: section.isComplete(dealer),
      path: section.route.getPath(dealer),
    }));
    result.push({
      name: 'Confirm launch',
      complete: isReadyToLaunch(dealer),
      path: ROUTES.DEALER_DASHBOARD_REVIEW.getPath(),
    });
    return result;
  }, [dealer]);

  const renderNav = () => (
    <div className={styles.pageNav}>
      <div className="spacer" />
      <Button
        className={styles.pageNavButton}
        label="Continue Setup"
        variant="primary"
        onClick={() => nextStep(undefined, { skipCompleteSteps: true })}
      />
    </div>
  );

  return (
    <div className={classNames(styles.stepContainer, styles.homeStep)}>
      <div className={styles.stepTitle}>
        {initialWelcome
          ? 'Welcome To Upstart Auto Retail Launch Pad!'
          : "You're making some great progress!"}{' '}
      </div>
      <div className={styles.stepSubtitle}>
        This is the central hub where you keep track of your progress in Upstart
        Auto Retail Quick Launch and pick up exactly where you left off last
        time.
      </div>
      <div className={styles.stepCard}>
        {initialWelcome && (
          <>
            <h1>We&apos;re here to help you along the way!</h1>
            <p>
              Throughout this setup and launch process there may be terms, or
              actions presented that seem complicated. We&apos;ve got you
              covered though with our improved Contextual Help. Look for tool
              tips with helpful information, and check out our quick guides.
            </p>
            <div className={classNames(styles.stepRow, styles.examples)}>
              <div className={styles.tooltipQuickGuideExample}>
                <h2>Tooltip Example</h2>
                <div className={styles.tooltipExampleOverlay}>
                  <div />
                  Try hovering over the icon to view the tooltip!
                </div>
                <TextField
                  label="Tooltip Example"
                  tooltip="An example tooltip with information that helps explain what this field is about"
                  value="example-tooltip@upstart.com"
                  width="md"
                  disabled
                />
              </div>
              <div className={styles.tooltipQuickGuideExample}>
                <h2>Quick Guide Example</h2>
                <TextField
                  label="Quick Guide Example"
                  value="example-guide@upstart.com"
                  width="md"
                  disabled
                />
                <div className={styles.quickGuideExampleOverlay} />
                <QuickGuide
                  label="Not sure what a quick guide is?"
                  action="Show Me An Example"
                  guideTitle="An example guide"
                >
                  <p>
                    Quick guides often show an in-depth description, or
                    walkthrough with images, that help you understand
                    what&apos;s needed to complete the step.
                  </p>
                </QuickGuide>
              </div>
            </div>
            <br />
          </>
        )}

        <h1>
          These are some things you might want to have on hand before you start
        </h1>
        <Checkbox
          className={styles.todoCheckbox}
          label="'UpstartAutoRetailAdmin' CRM Admin Account Login Details"
        />
        <Checkbox
          className={styles.todoCheckbox}
          label="F&I System Dealer ID"
        />
        <Checkbox
          className={styles.todoCheckbox}
          label="Trade In System Admin Login Details"
        />
      </div>
      {initialWelcome ? (
        renderNav()
      ) : (
        <div className={styles.stepCard}>
          <div className={styles.stepRow}>
            <h1>My Progress</h1>
            <div className={styles.progressBar}>
              <div
                className={styles.progressBarFill}
                style={{ width: `${stepCompletePercent * 100}%` }}
              />
              <div className={styles.progressTooltipContainer}>
                <div className={styles.progressTooltip}>
                  {`${(stepCompletePercent * 100).toFixed(0)}% complete!${
                    stepCompletePercent >= 0.5 && stepCompletePercent < 1
                      ? " You're almost there!"
                      : ''
                  }`}
                  <div className={styles.progressTooltipArrow} />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.sectionProgress}>
            {sectionProgress.map(({ name, complete, path }) => (
              <LinkButton
                key={name}
                className={styles.sectionProgressItem}
                variant="minimal"
                to={path}
              >
                <div
                  className={classNames(
                    styles.sectionProgressStatus,
                    complete && styles.complete,
                  )}
                >
                  {complete && (
                    <img src={checkGreenCircleImage} alt="Complete" />
                  )}
                </div>
                {name}
              </LinkButton>
            ))}
          </div>
          {renderNav()}
        </div>
      )}
    </div>
  );
};
